import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import taskRequest from "@/api/task-request";
import { getFacilityKeyForPortal, getPatientKey } from "@/store/profile";
import { useTaskStore } from "@/store/task";
import { useIntakeProgressStore, getIntakeTaskKey } from "@/store/intakeProgress";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { navigateToFormHandler } from "@/utils/formHandlerLaunch";
import {
  buildFirstFormHandlerLaunchPayload,
  loadPendingFormTasks,
} from "@/utils/navigateToFormHandler";

/**
 * Last step of an insurance-only (INS) task: the financial / insurance pages hand off here.
 * Completes the portal task (there is no history, so no case history to complete), then opens
 * the first pending form or the thank-you page.
 */
export function InsuranceCompletePage() {
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  const complete = useCallback(async () => {
    setFailed(false);
    const { activePortalTaskId, setTaskItem, setPortalCaseHistoryFlow } = useTaskStore.getState();
    const facilityKey = getFacilityKeyForPortal();
    const patientKey = getPatientKey();
    if (!activePortalTaskId || !facilityKey || !patientKey) {
      navigate("/", { replace: true });
      return;
    }
    try {
      await taskRequest.request.updateTaskComplete(facilityKey, patientKey, activePortalTaskId);
      setTaskItem(undefined);
      const payload = buildFirstFormHandlerLaunchPayload(
        await loadPendingFormTasks(facilityKey, patientKey)
      );
      if (payload) {
        // TaskCompletePage does this cleanup on the no-forms path.
        useIntakeProgressStore.getState().resetTask(getIntakeTaskKey());
        setPortalCaseHistoryFlow(false);
        navigateToFormHandler(navigate, payload, { replace: true });
      } else {
        navigate("/complaint/task-complete", { replace: true });
      }
    } catch {
      setFailed(true);
    }
  }, [navigate]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void complete();
  }, [complete]);

  return (
    <div className="wizard-page w-full px-4 py-10 text-center">
      <div className="wizard-page__content mx-auto max-w-md pb-4">
        {failed ? (
          <p className="text-sm text-gray-600">
            We couldn't save your insurance information. Please try again.
          </p>
        ) : (
          <p className="text-sm text-gray-600">Saving your information…</p>
        )}
      </div>
      {failed && (
        <WizardActionsBar>
          <span />
          <Button type="button" onClick={() => void complete()}>
            Try again
          </Button>
        </WizardActionsBar>
      )}
    </div>
  );
}
